import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowUpRight,
  IndianRupee,
  Package,
  Receipt,
  ShoppingBag,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { apiGet } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { useAuth } from '@/auth/AuthProvider';
import { Card, SectionHeader } from '@/components/ui/Card';
import { Money } from '@/components/ui/Money';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pill, StatusPill } from '@/components/ui/Pill';
import { StatCard } from '@/components/ui/StatCard';
import { ErrorState, LoadingState } from '@/components/ui/States';
import type { BillRow, DashboardSnapshot } from '@/types/api';
import { fmtRelative } from '@/utils/format';

/**
 * The Dashboard is the landing page for everyone — owner, manager, CA.
 *
 * It's the same layout for all three but the numbers come from
 * `/reports/dashboard` which is server-side scoped to the tenant. We
 * deliberately keep the layout unopinionated about role so the panel
 * works the same on mobile, tablet and desktop. Mobile collapses the
 * 4-up stat row to 2x2; desktop fans out the recent-bills + alerts
 * into a 2-column grid below the fold.
 */
export function DashboardPage() {
  const { me } = useAuth();
  const greeting = useGreeting();

  const dashQuery = useQuery({
    queryKey: ['report', 'dashboard'],
    queryFn: () => apiGet<DashboardSnapshot>(endpoints.reportDashboard),
  });

  const lowStockQuery = useQuery({
    queryKey: ['stock', 'low', { limit: 6 }],
    queryFn: () =>
      apiGet<{ items: { id: number; name: string; stock: number; min_stock_qty: number }[] }>(
        endpoints.stockLow,
        { per_page: 6 },
      ),
  });

  const billsQuery = useQuery({
    queryKey: ['bills', { limit: 6 }],
    queryFn: () =>
      apiGet<{ items: BillRow[] } | BillRow[]>(endpoints.bills, { per_page: 6 }),
  });

  if (dashQuery.isLoading) return <LoadingState />;
  if (dashQuery.isError) {
    return (
      <ErrorState
        title="Couldn't load dashboard"
        description={(dashQuery.error as Error)?.message}
        onRetry={() => dashQuery.refetch()}
      />
    );
  }

  const data = dashQuery.data ?? {};
  const lowStock = (lowStockQuery.data?.items ?? []).slice(0, 6);
  const recentBills = (Array.isArray(billsQuery.data)
    ? billsQuery.data
    : billsQuery.data?.items ?? []
  ).slice(0, 6);

  const trialDays = data.subscription_days_left ?? null;

  return (
    <>
      <PageHeader
        title={`${greeting}${me?.name ? `, ${firstName(me.name)}` : ''}`}
        description="Here's what's happening at your shop today."
      />

      {/* Trial / subscription banner */}
      {trialDays !== null && trialDays <= 7 && trialDays >= 0 && (
        <TrialBanner daysLeft={trialDays} />
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        <StatCard
          label="Today's revenue"
          value={<Money value={data.today_revenue ?? 0} />}
          icon={<IndianRupee className="h-5 w-5" />}
        />
        <StatCard
          label="Bills today"
          value={data.today_bills ?? 0}
          icon={<Receipt className="h-5 w-5" />}
        />
        <StatCard
          label="Pending dues"
          value={<Money value={data.pending_dues ?? 0} />}
          icon={<ShoppingBag className="h-5 w-5" />}
          delta={(data.pending_dues ?? 0) > 0 ? 'Tap Customers to remind' : 'All clear'}
          deltaTone={(data.pending_dues ?? 0) > 0 ? 'warn' : 'success'}
        />
        <StatCard
          label="Low stock"
          value={data.low_stock_count ?? 0}
          icon={<Package className="h-5 w-5" />}
          delta={(data.low_stock_count ?? 0) > 0 ? 'Restock soon' : 'Fully stocked'}
          deltaTone={(data.low_stock_count ?? 0) > 0 ? 'warn' : 'success'}
        />
      </div>

      {/* Two-column section: revenue chart + low-stock list */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2">
          <Card>
            <SectionHeader
              title="Revenue last 7 days"
              description="Daily totals across all branches."
              actions={
                <Link
                  to="/reports/revenue"
                  className="text-sm font-medium text-brand-700 hover:text-brand-800 inline-flex items-center gap-1"
                >
                  Open analytics <ArrowUpRight className="h-4 w-4" />
                </Link>
              }
            />
            <RevenueChart points={data.weekly_revenue ?? []} />
          </Card>
        </div>
        <Card>
          <SectionHeader
            title="Low stock"
            actions={
              <Link
                to="/stock/low"
                className="text-sm font-medium text-brand-700 hover:text-brand-800"
              >
                See all
              </Link>
            }
          />
          {lowStockQuery.isLoading ? (
            <p className="text-sm text-ink-soft">Loading…</p>
          ) : lowStock.length === 0 ? (
            <p className="text-sm text-ink-soft">Nothing to restock 🎉</p>
          ) : (
            <ul className="space-y-2">
              {lowStock.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center justify-between gap-3 p-2 -mx-2 rounded-lg hover:bg-surface-2"
                >
                  <div className="min-w-0">
                    <Link
                      to={`/products/${p.id}`}
                      className="text-sm font-medium text-ink truncate hover:text-brand-700"
                    >
                      {p.name}
                    </Link>
                    <div className="text-xs text-ink-soft">
                      {p.stock} left · min {p.min_stock_qty}
                    </div>
                  </div>
                  <Pill tone={p.stock <= 0 ? 'danger' : 'warn'}>
                    <AlertTriangle className="h-3 w-3" />
                    {p.stock <= 0 ? 'Out' : 'Low'}
                  </Pill>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Recent bills */}
      <Card>
        <SectionHeader
          title="Recent bills"
          actions={
            <Link
              to="/bills"
              className="text-sm font-medium text-brand-700 hover:text-brand-800"
            >
              View all
            </Link>
          }
        />
        {billsQuery.isLoading ? (
          <p className="text-sm text-ink-soft">Loading…</p>
        ) : recentBills.length === 0 ? (
          <p className="text-sm text-ink-soft">No bills yet today.</p>
        ) : (
          <ul className="divide-y divide-line -mx-5">
            {recentBills.map((b) => (
              <li key={b.id}>
                <Link
                  to={`/bills/${b.id}`}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-surface-2"
                >
                  <div className="h-9 w-9 rounded-lg bg-brand-500/10 text-brand-700 grid place-items-center">
                    <Receipt className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink truncate">
                      {b.customer?.name ||
                        b.customer_name_snapshot ||
                        'Walk-in customer'}
                    </div>
                    <div className="text-xs text-ink-soft">
                      {b.bill_number} · {fmtRelative(b.created_at)}
                    </div>
                  </div>
                  <StatusPill status={b.status} />
                  <Money value={b.total} className="font-semibold" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}

/* ───────────────────── helpers ───────────────────── */

function useGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function firstName(full: string) {
  return full.trim().split(/\s+/)[0];
}

function TrialBanner({ daysLeft }: { daysLeft: number }) {
  const tone = daysLeft <= 2 ? 'danger' : 'warn';
  const text =
    daysLeft <= 0
      ? 'Trial expired — upgrade to keep billing'
      : daysLeft === 1
        ? 'Trial ends tomorrow'
        : `Trial ends in ${daysLeft} days`;
  return (
    <div
      className={`mb-6 rounded-xl border p-3 flex items-center gap-3 ${
        tone === 'danger'
          ? 'bg-danger/10 border-danger/30'
          : 'bg-warn/10 border-warn/30'
      }`}
    >
      <AlertTriangle className={tone === 'danger' ? 'text-danger' : 'text-warn'} />
      <div className="flex-1 text-sm text-ink">{text}</div>
      <Link
        to="/subscription"
        className="text-sm font-semibold text-brand-700 hover:text-brand-800"
      >
        Choose a plan →
      </Link>
    </div>
  );
}

function RevenueChart({ points }: { points: { date: string; total: number }[] }) {
  if (points.length === 0) {
    return (
      <div className="h-56 grid place-items-center text-sm text-ink-soft">
        Not enough data yet — bills you make today will start filling this in.
      </div>
    );
  }
  const data = points.map((p) => ({
    label: new Date(p.date).toLocaleDateString('en-IN', { weekday: 'short' }),
    total: Number(p.total),
  }));
  return (
    <div className="h-56">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--line))" />
          <XAxis
            dataKey="label"
            stroke="rgb(var(--ink-soft))"
            tickLine={false}
            axisLine={false}
            fontSize={12}
          />
          <YAxis
            stroke="rgb(var(--ink-soft))"
            tickLine={false}
            axisLine={false}
            fontSize={12}
            width={50}
            tickFormatter={(v) => `₹${Math.round(v / 1000)}k`}
          />
          <Tooltip
            cursor={{ fill: 'rgb(var(--surface-2))' }}
            contentStyle={{
              backgroundColor: 'rgb(var(--surface))',
              border: '1px solid rgb(var(--line))',
              borderRadius: 8,
              fontSize: 12,
            }}
            formatter={(v: number) => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']}
          />
          <Bar dataKey="total" fill="rgb(var(--brand-500))" radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
